/**
 * The signed-in boot call (job #892).
 *
 * Tom's ruling, 2026-09-23: "the start of the app should be doing one to 2
 * database lookups and that's it." Job #860's door got a stranger there; a
 * returning signed-in learner still made thirteen before the first word, one
 * per composable that wanted to know something about them. `POST
 * /api/me/boot` answers all of it in one request, and this module is the
 * client half:
 *
 *  - `startSignedInBoot` fires it at the earliest moment a stored session and
 *    a course can be named (App.vue, where the bundle used to be warmed);
 *  - `bootFetch` stands in for `fetch` at the /api call sites the boot
 *    answers. It waits for that one answer if the boot is still streaming
 *    and hands back the route's own response, or passes through;
 *  - `bootLearner`, `bootEnrollment` and `bootInfinitePlay` do the same for
 *    the three PostgREST reads the boot also carries.
 *
 * The response is a STREAM, one JSON line per answer (the wire is described
 * in api/me/boot.ts). Each asker waits for its own line, not for the whole
 * boot: the learner row and the course position arrive long before the
 * entitlement grid, and the first word needs the first two, not the third.
 *
 * What it never does: answer for anybody else. Every answer is tied to the
 * exact access token the boot was made with, and a request carrying any other
 * token goes to the network. Nor does it answer late: answers stand for
 * BOOT_ANSWER_TTL_MS after the boot finished and never after a write the
 * boot did not make, so a purchase, a code redeem or a sign-out is always
 * asked afresh. When the boot fails, every caller asks for itself exactly as
 * it did before.
 */

import { firstSound } from './firstSoundSignal'

/** How long the boot's answers stand in for their routes once it has finished. */
export const BOOT_ANSWER_TTL_MS = 15_000
/**
 * How long, from the boot's start, a reader on the first-sound path waits for
 * its line before asking for itself (job #298; Tom, 2026-10-16: never more
 * than 3 s, cold servers and a busy database included). A boot held up by a
 * cold function or a slow read used to hold the learner, the position and the
 * first window with it: +3 s on the boot was +3 s to the first sound, measured.
 * Money and bundle readers are not on that path and keep waiting as before.
 */
export const BOOT_FIRST_SOUND_PATIENCE_MS = 1_000
let firstSoundPatienceMs = BOOT_FIRST_SOUND_PATIENCE_MS
/** The boot may carry the course bundle, so it gets the bundle's own budget. */
const BOOT_TIMEOUT_MS = 20_000

interface Snapshot {
  userId: string | null
  course: string | null
  /** undefined = not arrived; null = the server found no row. */
  learner?: (Record<string, unknown> & { id: string; verified_emails: string[] }) | null
  enrollment?: Record<string, unknown> | null
  infinitePlay?: Record<string, boolean>
  bundleKept?: boolean
  /** The position read failed server-side: ask for it yourself. */
  positionUnknown?: boolean
  responses: Record<string, { status: number; body: unknown }>
  /** Routes the boot says it will not answer. */
  skipped: Set<string>
  /** The first window is written, or will not be (job #298). */
  firstWindowSettled?: boolean
}

interface BootState {
  token: string | null
  course: string | null
  have: string | null
  /** The offer is decided (sent, or there is none). Readers ask before it is. */
  haveSettled: boolean
  snap: Snapshot
  /** The stream has ended — cleanly or not. Anything absent now will not come. */
  done: boolean
  /** The stream ended cleanly with its `done` line. */
  complete: boolean
  endedAt: number
  dead: boolean
  spent: Set<string>
  wake: Array<() => void>
  /** The boot was asked for the first window (job #299/#298). */
  window: boolean
  /** Date.now() when the boot was started, for BOOT_FIRST_SOUND_PATIENCE_MS. */
  startedAt: number
}

let state: BootState | null = null

/**
 * THE FIRST WINDOW'S CLIPS (jobs #299/#298). The boot names presigned S3 URLs
 * for the first window's opening clips; they are fetched the moment that line
 * lands — straight from S3, no Vercel function, no Range dance — and handed to
 * the sink the player installs (AudioCache.adoptBytes), so the first sound
 * makes no audio request of its own.
 */
/** `eager`: decode now (the window's opening clips); else hold the bytes until asked. */
type AudioSink = (id: string, bytes: ArrayBuffer, eager: boolean) => Promise<boolean>
/** How many of the window's clips, in play order, are decoded at once: the first cycle's. */
const EAGER_CLIPS = 3
let audioSink: AudioSink | null = null
const clipBytes = new Map<string, Promise<ArrayBuffer | null>>()
const clipEager = new Set<string>()
const clipAdopted = new Map<string, Promise<boolean>>()

function adoptClip(id: string): Promise<boolean> {
  const prior = clipAdopted.get(id)
  if (prior) return prior
  const bytes = clipBytes.get(id)
  const sink = audioSink
  if (!bytes || !sink) return Promise.resolve(false)
  const p = bytes.then((b) => (b ? sink(id, b, clipEager.has(id)).catch(() => false) : false))
  clipAdopted.set(id, p)
  return p
}

/** Where the boot's clip bytes go. Clips that arrived before the sink are handed over now. */
export function setBootAudioSink(sink: AudioSink | null): void {
  audioSink = sink
  if (sink) for (const id of clipBytes.keys()) void adoptClip(id)
}

/** Is this clip on its way from the boot's S3 URLs? Resolves true once it is playable from memory. */
export function bootClipPending(id: string): Promise<boolean> | null {
  return clipBytes.has(id) ? adoptClip(id) : null
}

function fetchWindowClips(urls: Record<string, unknown>): void {
  for (const [id, url] of Object.entries(urls)) {
    if (clipBytes.has(id) || typeof url !== 'string' || !url.startsWith('https://')) continue
    // The line lists clips in play order: the opening few are fetched and
    // decoded at once. The rest wait for the first sound (job #298): on slow
    // 4G (~200 KB/s) nine more clips, ~170 KB, shared the pipe with the
    // JavaScript and the catalogue the player mounts on, measured +0.8 s.
    const eager = clipEager.size < EAGER_CLIPS
    if (eager) clipEager.add(id)
    const get = () => fetch(url, { mode: 'cors', credentials: 'omit' })
      .then((r) => (r.ok ? r.arrayBuffer() : null))
      .catch(() => null)
    clipBytes.set(id, eager ? get() : firstSound().then(get))
    if (audioSink) void adoptClip(id)
  }
}

export interface StartBootOptions {
  /** The current session: access token + auth uid, or null when signed out. */
  session: () => Promise<{ token: string; userId: string } | null>
  course: string | null
  /** The version key of the bundle this device holds for `course`, if valid for this caller. */
  have?: (userId: string) => Promise<string | null>
  apiBase?: string
  /** The course the visitor pointed at (URL, demo, remembered), for the early boot's note (job #298). */
  intent?: string | null
}

/**
 * THE EARLY BOOT (job #298). index.html fires this same POST from an inline
 * script the moment the document arrives — before the app's JavaScript has
 * downloaded, parsed and run, which on a phone on 4G is a second or more —
 * and leaves the in-flight response on `window.__ssiEarlyBoot`. The app adopts
 * it instead of making its own call when it was made with the very token the
 * app's session holds, and for the course the app would name (or a course
 * where the app would have named none, which only adds answers).
 *
 * The inline script cannot read IndexedDB, so each boot leaves a note in
 * localStorage (EARLY_BOOT_NOTE_KEY) of the bundle key it offered, and the
 * next open's early boot offers it again when the user and course still match;
 * with no note it sends `bundle: false`, as a device with no copy does. A stale
 * key is harmless: the bundle reader only trusts a key that matches the record
 * it actually holds (bootOffered), and otherwise asks the network as before.
 */
export interface EarlyBoot {
  token: string
  userId: string
  course: string | null
  have: string | null
  res: Promise<Response | null>
}

export const EARLY_BOOT_NOTE_KEY = 'ssi-early-boot-note'

/** What index.html reads to replay the body: whose, what they meant, what was sent. */
export interface EarlyBootNote { u: string; intent: string | null; course: string | null; have: string | null }

function writeEarlyBootNote(note: EarlyBootNote): void {
  try { localStorage.setItem(EARLY_BOOT_NOTE_KEY, JSON.stringify(note)) } catch { /* no storage: the next open just boots late */ }
}

/** The early boot, taken once, if it stands for this session and course. Null = make your own call. */
export function takeEarlyBoot(
  session: { token: string; userId: string },
  course: string | null,
  holder: { __ssiEarlyBoot?: EarlyBoot } = globalThis as never,
): EarlyBoot | null {
  const early = holder.__ssiEarlyBoot
  if (!early) return null
  delete holder.__ssiEarlyBoot
  if (early.token !== session.token || early.userId !== session.userId) return null
  if (early.course !== course && course !== null) return null
  return early
}

function notify(s: BootState): void {
  const w = s.wake
  s.wake = []
  for (const f of w) f()
}

function applyLine(s: BootState, line: Record<string, unknown>): void {
  if (line.v === 1) {
    s.snap.userId = typeof line.userId === 'string' ? line.userId : null
    s.snap.course = typeof line.course === 'string' ? line.course : null
  } else if ('learner' in line) {
    s.snap.learner = (line.learner as Snapshot['learner']) ?? null
  } else if ('enrollment' in line) {
    s.snap.enrollment = (line.enrollment as Snapshot['enrollment']) ?? null
    s.snap.infinitePlay = (line.infinitePlay as Snapshot['infinitePlay']) ?? {}
  } else if (typeof line.k === 'string') {
    s.snap.responses[line.k] = { status: Number(line.status), body: line.body }
  } else if (line.position === 'unknown') {
    s.snap.positionUnknown = true
  } else if (typeof line.skip === 'string') {
    s.snap.skipped.add(line.skip)
  } else if ('bundleKept' in line) {
    s.snap.bundleKept = line.bundleKept === true
  } else if (line.firstWindowSettled === true) {
    s.snap.firstWindowSettled = true
  } else if ('audioUrls' in line) {
    if (line.audioUrls && typeof line.audioUrls === 'object') fetchWindowClips(line.audioUrls as Record<string, unknown>)
  } else if (line.done === true) {
    s.complete = true
  }
}

async function readStream(s: BootState, res: Response, expectedUserId: string, signal?: AbortSignal): Promise<void> {
  const decoder = new TextDecoder()
  let buf = ''
  let scanFrom = 0
  const take = (text: string, final: boolean) => {
    buf += text
    let nl: number
    while ((nl = buf.indexOf('\n', scanFrom)) >= 0) {
      const raw = buf.slice(0, nl)
      buf = buf.slice(nl + 1)
      scanFrom = 0
      if (!raw.trim()) continue
      const line = JSON.parse(raw) as Record<string, unknown>
      applyLine(s, line)
      // The server's word on who this is must be the session's own, or
      // nothing it says is used.
      if (line.v === 1 && s.snap.userId !== expectedUserId) {
        s.dead = true
        throw new Error('boot answered for another identity')
      }
      notify(s)
    }
    // An unfinished line's old prefix has already been checked for newlines.
    scanFrom = buf.length
    if (final && buf.trim()) { applyLine(s, JSON.parse(buf)); buf = '' }
  }
  const reader = res.body?.getReader?.()
  if (!reader) { take(await res.text(), true); return }
  // An adopted early request never saw our signal: the deadline ends its body read instead, keeping what arrived whole.
  signal?.addEventListener('abort', () => { void reader.cancel().catch(() => {}) }, { once: true })
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    take(decoder.decode(value, { stream: true }), false)
  }
  take(decoder.decode(), true)
}

/** Fire the boot call. Idempotent: the first call wins for the life of the page. */
export function startSignedInBoot(opts: StartBootOptions): void {
  if (state || typeof fetch === 'undefined') return
  const s: BootState = {
    token: null, course: opts.course, have: null, haveSettled: false,
    snap: { userId: null, course: null, responses: {}, skipped: new Set() },
    done: false, complete: false, endedAt: 0, dead: false, spent: new Set(), wake: [], window: false,
    startedAt: Date.now(),
  }
  state = s
  void (async () => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), BOOT_TIMEOUT_MS)
    try {
      const session = await opts.session().catch(() => null)
      if (!session?.token) { s.dead = true; return }
      s.token = session.token
      const early = takeEarlyBoot(session, opts.course)
      const haveFor = (course: string | null) => (course && opts.have ? opts.have(session.userId).catch(() => null) : Promise.resolve(null))
      let res: Response | null = null
      if (early) {
        // Adopted: the course and the key are what the early call actually sent.
        s.course = early.course
        s.have = early.have
        s.window = !!early.course
        s.haveSettled = true
        notify(s)
        // The early call carries no deadline of its own (index.html fires it before this code loads): the boot's
        // 20 s ends the wait for it too, and the readers are then answered "no boot" rather than held for ever.
        const deadline = new Promise<null>((resolve) => ctrl.signal.addEventListener('abort', () => resolve(null), { once: true }))
        res = await Promise.race([early.res.catch(() => null), deadline])
        // The note for the next open, from this device's own record, behind the stream.
        void haveFor(early.course).then((have) => writeEarlyBootNote({ u: session.userId, intent: opts.intent ?? null, course: early.course, have }))
      }
      if (!res) {
        s.window = !!s.course
        if (!early) {
          s.have = await haveFor(opts.course)
          s.haveSettled = true
          notify(s)
          writeEarlyBootNote({ u: session.userId, intent: opts.intent ?? null, course: opts.course, have: s.have })
        }
        res = await fetch(`${opts.apiBase ?? ''}/api/me/boot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
          // No copy on the device: the boot does not carry the whole course in
          // front of the first word (job #287); the player fetches it after the
          // first sound. A device with a copy offers its key as before.
          // ...and, with a course, the first window: the round map, the resume
          // window and its clips' S3 URLs, so the player asks the network for
          // none of them (job #299; consumed here, job #298).
          body: JSON.stringify({ course: s.course, ...(s.have ? { have: s.have } : s.course ? { bundle: false } : {}), ...(s.course ? { firstWindow: true } : {}) }),
          signal: ctrl.signal,
        })
      }
      if (!res.ok) { s.dead = true; return }
      await readStream(s, res, session.userId, ctrl.signal)
    } catch {
      // A half-read stream keeps what arrived whole; anything missing is asked for real.
      if (s.snap.userId === null) s.dead = true
    } finally {
      clearTimeout(timer)
      s.done = true
      s.endedAt = Date.now()
      notify(s)
    }
  })()
}

/**
 * A signed-in learner opening another course (job #315). The page's boot is
 * for the course the page opened on, or there is none at all when they signed
 * in after the page loaded — so the remounted player fell back to the old
 * serial path: round map, then cycles, then each clip through the audio
 * proxy, every one a database round trip in front of the first sound. Tom,
 * signed in on his phone and opening Italian, sat through all of them, and
 * a sixteen-second database stall on top. A fresh boot for the new course
 * puts the switch on the page-load path: one request for the round map, the
 * resume window, the enrolment and the window's S3 clip URLs. Same course
 * as the boot already standing: nothing to do.
 */
export function restartSignedInBoot(opts: StartBootOptions): void {
  if (state && !state.dead && state.course === opts.course) return
  if (state) { state.dead = true; notify(state) }
  state = null
  startSignedInBoot(opts)
}

/** Forget the boot: sign-out, a different learner, or a write that changes the answers. */
export function invalidateSignedInBoot(): void {
  if (!state) return
  state.dead = true
  notify(state)
}

/** The boot, once `has` holds or it cannot hold any more. Null when the boot has nothing to say. */
async function when(has: (s: BootState) => boolean, patienceMs?: number): Promise<BootState | null> {
  const s = state
  if (!s) return null
  // With a patience, stop waiting that long after the boot began, line or no line.
  let late = false
  const deadline = patienceMs === undefined ? null : s.startedAt + patienceMs
  let timer: ReturnType<typeof setTimeout> | undefined
  const lateP = deadline === null ? null : new Promise<void>((r) => {
    timer = setTimeout(() => { late = true; r() }, Math.max(0, deadline - Date.now()))
  })
  try {
    while (!s.dead && !s.done && !late && !has(s)) {
      const woken = new Promise<void>((r) => s.wake.push(r))
      await (lateP ? Promise.race([woken, lateP]) : woken)
    }
  } finally {
    if (timer) clearTimeout(timer)
  }
  if (s.dead || s.snap.userId === null) return null
  if (s.done && Date.now() - s.endedAt > BOOT_ANSWER_TTL_MS) return null
  return has(s) ? s : null
}

function pathOf(input: RequestInfo | URL): string | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
    const base = typeof location !== 'undefined' ? location.origin : 'http://local'
    const u = new URL(raw, base)
    if (typeof location !== 'undefined' && u.origin !== location.origin) return null
    return u.pathname + u.search
  } catch {
    return null
  }
}

function bearerOf(init?: RequestInit): string | null {
  if (!init?.headers) return null
  const v = new Headers(init.headers).get('Authorization')
  return v?.startsWith('Bearer ') ? v.slice(7) : null
}

/** Could the boot answer this request at all? Decides whether we wait. */
function mayAnswer(key: string): boolean {
  if (key === 'GET /api/subscription' || key === 'GET /api/entitlement/user') return true
  if (key === 'POST /api/access/claim' || key === 'POST /api/auth/claim-account') return true
  const course = state?.course
  if (!course) return false
  const c = encodeURIComponent(course)
  return key === `GET /api/entitlement/user?course=${c}` || key === `GET /api/courses/${c}/bundle` ||
    key === `GET /api/courses/${c}/bundle?head=1` || isWindowKey(key)
}

/** The first window's two requests (job #299), when this boot was asked for them. */
function isWindowKey(key: string): boolean {
  const course = state?.course
  if (!state?.window || !course) return false
  const c = encodeURIComponent(course)
  return key === `GET /api/courses/${c}/round-map` || key.startsWith(`GET /api/courses/${c}/cycles?`)
}

/**
 * `fetch`, except that while a signed-in boot stands it answers the routes
 * the boot carried, for the token the boot was made with.
 */
export async function bootFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase()
  // A delta is carried under the bundle's usual boot key; `since` only names
  // the local base. The network fallback still receives the original URL.
  const path = pathOf(input)?.replace(/(\/bundle)\?since=[^&]+$/, '$1')
  const key = path ? `${method} ${path}` : null
  const plain = () => fetch(input, init)
  if (!state || state.dead || !key) return plain()
  if (!mayAnswer(key)) {
    // A write the boot did not make: whatever it changes, the boot predates.
    if (method !== 'GET') invalidateSignedInBoot()
    return plain()
  }
  // A POST with a body is an answer to a question the boot never asked
  // (the contest card's vouch/contest) — always the real route.
  if (method !== 'GET' && init?.body != null && init.body !== '') return plain()
  if (bearerOf(init) === null) return plain()
  // A window request the boot did not prepare (another `from`) stops waiting
  // when the boot says its window has settled, not at the end of the stream.
  const windowKey = isWindowKey(key)
  const answered = (b: BootState) => key in b.snap.responses || b.snap.skipped.has(key) || (windowKey && !!b.snap.firstWindowSettled)
  if (windowKey) {
    // On the first-sound path: a late boot does not hold the window. Past the
    // patience the network is asked too, and whichever answers first plays.
    const fromBoot = when(answered).then((b) => (b && b.token && bearerOf(init) === b.token && b.snap.responses[key] ? asResponse(b.snap.responses[key]) : null))
    const inTime = await when(answered, firstSoundPatienceMs)
    if (inTime) {
      const r = await fromBoot
      return r ?? plain()
    }
    const net = plain()
    // A network blip must not beat the boot's good answer: if the extra request fails, the boot's answer
    // (which may land a moment later) still serves; only when neither has one does the failure surface.
    const netOrBoot = net.catch(() => fromBoot.then((r) => r ?? net))
    return Promise.race([netOrBoot, fromBoot.then((r) => r ?? netOrBoot)])
  }
  const s = await when(answered)
  if (!s || !s.token || bearerOf(init) !== s.token) return plain()
  const hit = s.snap.responses[key]
  if (!hit) return plain()
  if (method !== 'GET') {
    if (s.spent.has(key)) return plain()
    s.spent.add(key)
  }
  return asResponse(hit)
}

function asResponse(hit: { status: number; body: unknown }): Response {
  return new Response(JSON.stringify(hit.body), {
    status: hit.status,
    headers: { 'Content-Type': 'application/json', 'X-Ssi-Boot': '1' },
  })
}

/**
 * Did the boot offer the server this exact bundle key for `course`, and the
 * server keep it? True only when the server, having run its own gate for this
 * caller, sent the head and no replacement bundle.
 */
export async function bootConfirmsBundle(course: string, key: string): Promise<boolean> {
  if (!state || state.course !== course || state.dead) return false
  const s = await when((b) => b.snap.bundleKept !== undefined)
  return !!s && s.have === key && s.snap.bundleKept === true
}

/**
 * Did this page's boot offer the server exactly this bundle key for `course`?
 * The first bundle reader runs before the boot has read its own cache record,
 * so this waits for the offer to be decided (a few ms of IndexedDB).
 */
export async function bootOffered(course: string, key: string): Promise<boolean> {
  const s = state
  if (!s || s.course !== course) return false
  while (!s.dead && !s.done && !s.haveSettled) await new Promise<void>((r) => s.wake.push(r))
  return !s.dead && s.have === key
}

/** The course's current content/shape versions, from the boot's head line. Null = ask yourself. */
export async function bootHead(course: string): Promise<{ contentVersion: string | number; scriptShapeVersion: number } | null> {
  if (!state || state.course !== course) return null
  const key = `GET /api/courses/${encodeURIComponent(course)}/bundle?head=1`
  const s = await when((b) => key in b.snap.responses || b.snap.skipped.has(key))
  const hit = s?.snap.responses[key]
  return hit?.status === 200 ? (hit.body as { contentVersion: string | number; scriptShapeVersion: number }) : null
}

/**
 * The bundle the server sent INSTEAD of keeping the offered one — the tier
 * changed under a record whose versions still matched. Null when it kept it,
 * or when the boot cannot say.
 */
export async function bootReplacementBundle<T = unknown>(course: string): Promise<T | null> {
  if (!state || state.course !== course) return null
  const s = await when((b) => b.snap.bundleKept !== undefined)
  if (!s || s.snap.bundleKept !== false) return null
  const hit = s.snap.responses[`GET /api/courses/${encodeURIComponent(course)}/bundle`]
  return hit?.status === 200 ? (hit.body as T) : null
}

/** Did the boot say it did NOT keep the offered bundle (the tier changed)? Its replacement is no longer sent (job #196). */
export async function bootRefusedKeptBundle(course: string): Promise<boolean> {
  if (!state || state.course !== course) return false
  const s = await when((b) => b.snap.bundleKept !== undefined)
  return !!s && s.snap.bundleKept === false
}

/** The learner row + verified emails for `userId`, from the boot. Undefined = ask yourself. */
export async function bootLearner(userId: string): Promise<(Record<string, unknown> & { id: string; verified_emails: string[] }) | undefined> {
  const s = await when((b) => b.snap.learner !== undefined, firstSoundPatienceMs)
  if (!s || s.snap.userId !== userId || !s.snap.learner) return undefined
  return s.snap.learner
}

/** This learner's enrollment row for `course` (null = not enrolled). Undefined = ask yourself. */
export async function bootEnrollment(learnerId: string, course: string): Promise<Record<string, unknown> | null | undefined> {
  if (!state || state.course !== course) return undefined
  const s = await when((b) => b.snap.enrollment !== undefined || b.snap.learner === null || !!b.snap.positionUnknown, firstSoundPatienceMs)
  if (!s || s.snap.learner?.id !== learnerId || s.snap.enrollment === undefined) return undefined
  return s.snap.enrollment
}

/** hasReachedInfinitePlay(cursor, course) as the boot asked it. Undefined = ask yourself. */
export async function bootInfinitePlay(course: string, cursor: string | null): Promise<boolean | undefined> {
  if (!cursor || !state || state.course !== course) return undefined
  const s = await when((b) => b.snap.infinitePlay !== undefined || b.snap.learner === null || !!b.snap.positionUnknown, firstSoundPatienceMs)
  const v = s?.snap.infinitePlay?.[cursor]
  return typeof v === 'boolean' ? v : undefined
}

/** Test isolation only: a short patience, so no test sleeps through the real one. */
export function __setFirstSoundPatienceForTests(ms: number): void {
  firstSoundPatienceMs = ms
}

/** Test isolation only. */
export function __resetSignedInBootForTests(): void {
  firstSoundPatienceMs = BOOT_FIRST_SOUND_PATIENCE_MS
  audioSink = null
  clipBytes.clear()
  clipAdopted.clear()
  clipEager.clear()
  state = null
}
